import { useEffect, useState } from "react";
import { Bell, BellOff, Send, Loader } from "lucide-react";
import { toast } from "sonner";
import {
  isPushSupported,
  getNotificationPermission,
  getExistingSubscription,
  subscribeToPush,
  unsubscribeFromPush,
  sendTestPush,
} from "@/lib/pushClient";

function isIOS() {
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua);
}
function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

export default function PushNotificationsCard() {
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [checking, setChecking] = useState(true);

  const needsInstallOnIOS = isIOS() && !isStandalone();

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      const sup = isPushSupported();
      if (cancelled) return;
      setSupported(sup);
      if (!sup) {
        setChecking(false);
        return;
      }
      setPermission(getNotificationPermission());
      try {
        const sub = await getExistingSubscription();
        if (!cancelled) setSubscribed(!!sub);
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setChecking(false);
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleEnable = async () => {
    setLoading(true);
    try {
      await subscribeToPush();
      setPermission(getNotificationPermission());
      setSubscribed(true);
      toast.success("¡Notificaciones activadas en este dispositivo!");
    } catch (e) {
      toast.error(e.message || "No se pudieron activar las notificaciones.");
      setPermission(getNotificationPermission());
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    setLoading(true);
    try {
      await unsubscribeFromPush();
      setSubscribed(false);
      toast.success("Notificaciones desactivadas en este dispositivo.");
    } catch {
      toast.error("No se pudieron desactivar.");
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await sendTestPush();
      toast.success("Enviando notificación de prueba… revisa tu móvil.");
    } catch {
      toast.error("No se pudo enviar la prueba.");
    } finally {
      setTesting(false);
    }
  };

  if (checking) {
    return (
      <div className="mb-4 p-4 rounded-2xl bg-[#FDFAF4] border border-[#5B3E2E]/8 flex items-center gap-3">
        <Loader size={16} className="text-[#5B3E2E]/40 animate-spin" />
        <p className="text-xs text-[#5B3E2E]/50">Comprobando notificaciones…</p>
      </div>
    );
  }

  if (!supported) {
    return (
      <div className="mb-4 p-4 rounded-2xl bg-[#FDFAF4] border border-[#5B3E2E]/8">
        <div className="flex items-start gap-3">
          <BellOff size={18} className="text-[#5B3E2E]/40 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-[#5B3E2E]">
              Notificaciones no disponibles
            </p>
            <p className="text-xs text-[#5B3E2E]/50 mt-1">
              Tu navegador no soporta notificaciones push.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (needsInstallOnIOS) {
    return (
      <div
        data-testid="push-card"
        className="mb-4 p-4 rounded-2xl bg-[#FDFAF4] border border-[#5B3E2E]/8"
      >
        <div className="flex items-start gap-3">
          <Bell size={18} className="text-[#E48D57] mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-[#5B3E2E]">
              Instala Refugium para activar notificaciones
            </p>
            <p className="text-xs text-[#5B3E2E]/60 mt-1 leading-relaxed">
              En iPhone las notificaciones push solo funcionan si añades la app a
              tu pantalla de inicio. Pulsa{" "}
              <span className="font-medium">Compartir → "Añadir a pantalla de inicio"</span>{" "}
              en Safari y luego vuelve aquí.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (permission === "denied") {
    return (
      <div
        data-testid="push-card"
        className="mb-4 p-4 rounded-2xl bg-[#FDFAF4] border border-[#5B3E2E]/8"
      >
        <div className="flex items-start gap-3">
          <BellOff size={18} className="text-[#5B3E2E]/50 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-[#5B3E2E]">
              Notificaciones bloqueadas
            </p>
            <p className="text-xs text-[#5B3E2E]/60 mt-1 leading-relaxed">
              Has bloqueado las notificaciones en este dispositivo. Actívalas
              desde los ajustes del navegador y vuelve aquí.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="push-card"
      className="mb-4 p-4 rounded-2xl bg-gradient-to-br from-[#E48D57]/10 to-[#F0A82C]/5 border border-[#E48D57]/20"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-white/60 border border-[#E48D57]/20 flex items-center justify-center flex-shrink-0">
          <Bell size={18} className="text-[#E48D57]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#5B3E2E]">
            Notificaciones en el móvil
          </p>
          <p className="text-xs text-[#5B3E2E]/60 mt-0.5 leading-relaxed">
            {subscribed
              ? "Recibirás avisos de mensajes nuevos en salas y recordatorios de sesiones."
              : "Actívalas para no perderte mensajes en salas y recordatorios de tus sesiones."}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {subscribed ? (
              <>
                <button
                  data-testid="push-disable-btn"
                  onClick={handleDisable}
                  disabled={loading}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#5B3E2E]/15 px-3 py-1.5 text-xs font-medium text-[#5B3E2E] hover:bg-white transition-colors disabled:opacity-50"
                >
                  {loading ? (
                    <Loader size={12} className="animate-spin" />
                  ) : (
                    <BellOff size={12} />
                  )}
                  Desactivar
                </button>
                <button
                  data-testid="push-test-btn"
                  onClick={handleTest}
                  disabled={testing}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#5B3E2E] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#4a3225] transition-colors disabled:opacity-50"
                >
                  {testing ? (
                    <Loader size={12} className="animate-spin" />
                  ) : (
                    <Send size={12} />
                  )}
                  Enviar prueba
                </button>
              </>
            ) : (
              <button
                data-testid="push-enable-btn"
                onClick={handleEnable}
                disabled={loading}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#E48D57] px-4 py-2 text-xs font-semibold text-white hover:bg-[#c96a34] transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <Loader size={12} className="animate-spin" />
                ) : (
                  <Bell size={12} />
                )}
                Activar notificaciones
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
